/**
 * Whether a completed job's detached supply metadata resync can still report
 * (Phase 92, 92-25 review m2), for the job report's "Updating print file
 * names..." line.
 *
 * The answer turns false on its own at the bound (`finished_at` + the
 * resync's lease wait), and the poll stops at that same moment, so no fresh
 * data arrives to re-render the report. A one-shot timer re-renders it at the
 * deadline, which swaps the line for the "run the sync again" warning. A
 * resync answer (pending to 0, or its warning) ends the wait earlier through
 * the polled stats. No read of its own.
 */
import { useEffect, useReducer } from 'react';
import { supplyResyncDeadline } from '../lib/apply-types';

export function useAwaitingSupplyResync(
  status: string | null | undefined,
  stats: unknown,
  finishedAt: string | null | undefined,
): boolean {
  const deadline = supplyResyncDeadline(status, stats, finishedAt);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (deadline === null) return;
    const remaining = deadline - Date.now();
    if (remaining < 0) return;
    const id = window.setTimeout(rerender, remaining + 1);
    return () => window.clearTimeout(id);
  }, [deadline]);

  return deadline !== null && Date.now() <= deadline;
}
